const BANNED_DISPLAY_KEY_PREFIXES = [
    "backend",
    "candidate",
    "classification",
    "editorial",
    "production",
    "provenance",
    "receipt",
    "review",
    "rights",
    "selection",
    "source",
    "temporal",
];
export class UnsafeDisplayDataError extends TypeError {
    fields;
    constructor(fields) {
        const locations = fields.map(({ path }) => path).join(", ");
        super(`Display data contains backend or review metadata at: ${locations}`);
        this.name = "UnsafeDisplayDataError";
        this.fields = fields;
    }
}
function pathForProperty(parent, key) {
    return /^[A-Za-z_$][\w$]*$/.test(key)
        ? `${parent}.${key}`
        : `${parent}[${JSON.stringify(key)}]`;
}
function isBannedDisplayKey(key) {
    const normalizedKey = key.replace(/[^A-Za-z0-9]/gu, "").toLowerCase();
    return BANNED_DISPLAY_KEY_PREFIXES.some((prefix) => normalizedKey.startsWith(prefix));
}
/**
 * Finds forbidden project-side metadata at any depth. Cycles are tolerated so
 * the renderer boundary fails on unsafe keys rather than recursing forever.
 */
export function findUnsafeDisplayFields(value) {
    const fields = [];
    const visited = new WeakSet();
    function visit(candidate, path) {
        if (candidate === null || typeof candidate !== "object") {
            return;
        }
        if (visited.has(candidate)) {
            return;
        }
        visited.add(candidate);
        if (Array.isArray(candidate)) {
            candidate.forEach((item, index) => visit(item, `${path}[${index}]`));
            return;
        }
        for (const [key, nested] of Object.entries(candidate)) {
            const propertyPath = pathForProperty(path, key);
            if (isBannedDisplayKey(key)) {
                fields.push({ key, path: propertyPath });
            }
            visit(nested, propertyPath);
        }
    }
    visit(value, "$");
    return fields;
}
export function isDisplaySafe(value) {
    return findUnsafeDisplayFields(value).length === 0;
}
export function assertDisplaySafe(value) {
    const fields = findUnsafeDisplayFields(value);
    if (fields.length > 0) {
        throw new UnsafeDisplayDataError(fields);
    }
}
